import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';

export interface PostbackFilter {
  payout?: { op: string; value: number | [number, number] };
  query_params?: Array<{ name: string; op: string; value: string | string[] | [number, number] }>;
  $or?: PostbackFilter[];
}

export interface PostbackUrlEntry {
  event_types: string[];
  url: string;
  method?: 'GET' | 'POST';
  body?: unknown;                     // POST only: JSON template with {{token}} leaves
  headers?: Record<string, string>;   // POST only: templated header values
  event_mapping?: Record<string, string>;
  filter?: PostbackFilter;
}

export interface PostbackUrlConfig {
  enabled: boolean;
  entries?: PostbackUrlEntry[];
}

/**
 * {{token}}s available in a url postback's URL / POST body / headers — the keys the server puts on
 * the conversion payload it substitutes (server postback-ingest.controller.ts `conversionData`, plus
 * conversion_type / mapped_event / event_time). Client-maintained reference: keep in sync with that
 * payload. event_time is unix seconds.
 */
export const POSTBACK_TOKENS: { group: string; tokens: string[] }[] = [
  { group: 'Conversion', tokens: ['click_id', 'external_id', 'id', 'transaction_id', 'revenue', 'currency', 'event_type', 'conversion_type', 'mapped_event', 'event_time'] },
  { group: 'Attribution', tokens: ['offer_id', 'network_id', 'landing_page_id', 'flow_id', 'flow_path_id', 'rule_group_id', 'traffic_source_id', 'campaign_id', 'ad_id', 'ad_set_id', 'ad_account_id'] },
  { group: 'Visitor', tokens: ['country', 'region', 'device', 'os', 'browser', 'lang', 'ip', 'user_agent', 'referer'] },
  { group: 'Custom', tokens: ['v1', 'v2', 'v3', 'v4', 'v5', 'v6', 'v7', 'v8', 'v9', 'v10'] },
];

export interface PostbackFacebookEntry {
  event_types: string[];
  pixel_id: string;
  access_token: string;
  default_event_name?: string;
  filter?: PostbackFilter;
  event_mapping?: Record<string, string>;
}

export interface PostbackFacebookConfig {
  entries?: PostbackFacebookEntry[];
  pixel_id?: string;  // legacy
  access_token?: string;
  default_event_name?: string;
  allowed_event_names?: string[];
  filter?: PostbackFilter;
}

export interface PostbackBingEntry {
  event_types: string[];
  tag_id: string;      // numeric UET tag id
  api_token: string;   // CAPI token for that tag
  event_mapping?: Record<string, string>;
  filter?: PostbackFilter;
}

export interface PostbackBingConfig {
  entries?: PostbackBingEntry[];
}

export interface PostbackTwitterEntry {
  event_types: string[];
  pixel_id: string;
  pixel_token: string;
  /** The ad account's reporting currency (e.g. 'ILS'); the value is converted to it before
   *  sending. Generic server-side; only X exposes it in the UI today. */
  currency?: string;
  event_mapping?: Record<string, string>;
  filter?: PostbackFilter;
}

export interface PostbackTwitterConfig {
  entries?: PostbackTwitterEntry[];
}

export interface PostbackOpenAiEntry {
  event_types: string[];
  pid: string;
  api_key: string;
  default_event_type?: string;
  event_mapping?: Record<string, string>;
  filter?: PostbackFilter;
}

export interface PostbackOpenAiConfig {
  entries?: PostbackOpenAiEntry[];
}

export interface PostbackGoogleAdsEntry {
  event_types: string[];
  login_customer_id?: string;
  customer_id: string;
  conversion_action_id: string;
  refresh_token: string;
  event_mapping?: Record<string, string>;
  filter?: PostbackFilter;
}

export interface PostbackGoogleAdsConfig {
  entries?: PostbackGoogleAdsEntry[];
  filter?: PostbackFilter;
}

export interface PostbackUsage {
  traffic_sources: Array<{ id: string; name: string }>;
  offers: Array<{ id: string; name: string }>;
}

export interface Postback {
  id: string;
  workspace_id: string;
  name: string;
  type: 'url' | 'facebook_capi' | 'google_ads' | 'bing_capi' | 'twitter_capi' | 'openai_capi';
  config: PostbackUrlConfig | PostbackFacebookConfig | PostbackGoogleAdsConfig | PostbackBingConfig | PostbackTwitterConfig | PostbackOpenAiConfig;
  status: string;
  created_at: string;
  updated_at: string;
  usage?: { traffic_sources: number; offers: number };
}

export const postbacksService = {
  query: async (filters: { limit?: number; offset?: number; search?: string; where?: WhereCondition } = {}): Promise<{ data: Postback[]; total: number }> => {
    const where: WhereCondition | undefined = filters.where ?? (filters.search
      ? { postback: { name: { op: 'ilike', value: `%${filters.search}%` } } } : undefined);
    const payload: QueryPayload = {
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 100,
      order_by: 'postback.created_at',
      order: 'desc',
      ...(where && { where }),
    };
    const response = await api.post<QueryResponse<Postback>>('/postbacks/query', payload);
    return { data: response.data.data, total: response.data._meta?.total ?? 0 };
  },

  usage: async (id: string): Promise<PostbackUsage> => {
    const response = await api.get<ApiResponse<PostbackUsage>>(`/postbacks/${id}/usage`);
    return response.data.data!;
  },

  getById: async (id: string): Promise<Postback> => {
    const response = await api.get<ApiResponse<Postback>>(`/postbacks/${id}`);
    return response.data.data!;
  },

  create: async (data: Partial<Postback>): Promise<Postback> => {
    const response = await api.post<ApiResponse<Postback>>('/postbacks', data);
    return response.data.data!;
  },

  update: async (id: string, data: Partial<Postback>): Promise<Postback> => {
    const response = await api.patch<ApiResponse<Postback>>(`/postbacks/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/postbacks/${id}`);
  },

  unarchive: async (id: string): Promise<Postback> => {
    const response = await api.patch<ApiResponse<Postback>>(`/postbacks/${id}/unarchive`);
    return response.data.data!;
  },

  testFbPixel: async (data: {
    pixel_id: string;
    access_token: string;
    test_event_code?: string;
    event_name?: string;
  }): Promise<{ success: boolean; data: any }> => {
    const response = await api.post<ApiResponse<any>>('/postbacks/test', data);
    return { success: response.data.success, data: response.data.data };
  },

  testGoogleAds: async (data: {
    login_customer_id?: string;
    customer_id: string;
    conversion_action_id: string;
    refresh_token: string;
  }): Promise<{ success: boolean; data: any }> => {
    const response = await api.post<ApiResponse<any>>('/postbacks/test-google', data);
    return { success: response.data.success, data: response.data.data };
  },

  testTwitter: async (data: {
    pixel_id: string;
    pixel_token: string;
    event_name?: string;
  }): Promise<{ success: boolean; data: any }> => {
    const response = await api.post<ApiResponse<any>>('/postbacks/test-twitter', data);
    return { success: response.data.success, data: response.data.data };
  },

  testOpenai: async (data: {
    pid: string;
    api_key: string;
    event_type?: string;
  }): Promise<{ success: boolean; data: any }> => {
    const response = await api.post<ApiResponse<any>>('/postbacks/test-openai', data);
    return { success: response.data.success, data: response.data.data };
  },
};
